"use client";

import { useState, useEffect, useTransition } from "react";
import { useRouter } from "next/navigation";
import { formatDistanceToNow } from "date-fns";

interface NewsArticle {
  title: string;
  link: string;
  pubDate: Date;
  source: string;
}

interface NewsSidebarProps {
  companyNews: NewsArticle[];
  industryNews: NewsArticle[];
}

function NewsCard({ article }: { article: NewsArticle }) {
  const timeAgo = formatDistanceToNow(new Date(article.pubDate), { addSuffix: true });

  return (
    <a
      href={article.link}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
    >
      <h4 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 line-clamp-2">
        {article.title}
      </h4>
      <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
        {article.source} · {timeAgo}
      </p>
    </a>
  );
}

function NewsSection({ title, articles, accentColor }: { title: string; articles: NewsArticle[]; accentColor: string }) {
  if (articles.length === 0) return null;

  return (
    <div className="mb-4 last:mb-0">
      <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${accentColor}`}>
        {title}
      </h3>
      <div className="space-y-1">
        {articles.map((article, index) => (
          <NewsCard key={index} article={article} />
        ))}
      </div>
    </div>
  );
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=;path=/;max-age=0`;
}

export default function NewsSidebar({ companyNews, industryNews }: NewsSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  // Load collapsed state from localStorage on mount, sync cookie
  useEffect(() => {
    const stored = localStorage.getItem("news-sidebar-collapsed");
    if (stored !== null) {
      const collapsed = stored === "true";
      setIsCollapsed(collapsed);
      // Sync cookie to match localStorage (cookie "0" = expanded, absent = collapsed)
      if (collapsed) {
        deleteCookie("news-collapsed");
      } else {
        setCookie("news-collapsed", "0");
      }
    }
  }, []);

  // Measure first StatCard height to match collapsed button (once on mount)
  useEffect(() => {
    const statCard = document.querySelector('.grid.grid-cols-1.md\\:grid-cols-4 > div');
    if (statCard) {
      const h = statCard.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--stat-card-h', `${h}px`);
    }
  }, []);

  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("news-sidebar-collapsed", String(newState));

    if (newState) {
      // Collapsing — delete cookie (absent = collapsed by default)
      deleteCookie("news-collapsed");
    } else {
      // Expanding — set cookie to "0", refresh to fetch news server-side
      setCookie("news-collapsed", "0");
      startTransition(() => {
        router.refresh();
      });
    }
  };

  const hasNews = companyNews.length > 0 || industryNews.length > 0;

  // NOTE: never return null here. Doing so removes the toggle button along
  // with the column, so if a fetch comes back empty while expanded the whole
  // sidebar vanishes with no way to collapse it again. Instead we always
  // render the button and show an empty-state message when there's no news.

  return (
    <div className={`hidden sm:block flex-shrink-0 transition-all duration-200 ${isCollapsed ? "w-3" : "w-72"}`} style={isCollapsed ? { marginRight: 'calc(-1.5rem + 6px)' } : { marginRight: '-0.25rem' }}>
      <div className={`sticky top-8 ${isCollapsed ? "" : "bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4"}`}>
        <div className={`flex items-center justify-between ${isCollapsed ? "mb-0" : "mb-4"}`}>
          {!isCollapsed && (
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">News</h2>
          )}
          <button
            onClick={toggleCollapsed}
            className={`transition-colors text-zinc-400 hover:text-zinc-600 dark:text-zinc-600 dark:hover:text-zinc-400 ${isCollapsed ? "w-3 bg-transparent rounded border border-zinc-200 dark:border-zinc-800 flex items-center justify-center" : "p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"}`}
            style={isCollapsed ? { height: 'var(--stat-card-h)' } : undefined}
            title={isCollapsed ? "Show news" : "Hide news"}
          >
            {isCollapsed ? (
              // Chevron right (expand)
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              // Chevron left (collapse)
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>
        {!isCollapsed && (
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
            {isPending ? (
              <p className="text-sm text-zinc-400 dark:text-zinc-500 text-center py-4">Loading news…</p>
            ) : hasNews ? (
              <>
                <NewsSection
                  title="Company"
                  articles={companyNews}
                  accentColor="text-purple-600 dark:text-purple-400"
                />
                <NewsSection
                  title="Industry"
                  articles={industryNews}
                  accentColor="text-blue-600 dark:text-blue-400"
                />
              </>
            ) : (
              <p className="text-sm text-zinc-400 dark:text-zinc-500 text-center py-4">No news right now</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
